import React from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';

export type ProActionButtonVariant = 'danger' | 'ghost';

interface ProActionButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: ProActionButtonVariant;
  /** ionicons icon rendered before the label; omit for text-only buttons */
  icon?: string;
  /** show a spinner in place of the content (also disables the button) */
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /** extra classes (e.g. spacing) merged after the base/variant classes */
  className?: string;
}

const BASE_CLASSES =
  'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-60';

const VARIANT_CLASSES: Record<ProActionButtonVariant, string> = {
  danger: 'bg-red-600 text-white active:bg-red-700',
  ghost:
    'bg-transparent text-text-secondary dark:text-text-dark-secondary active:bg-black/5 dark:active:bg-white/5',
};

/**
 * Full-width action button for Pro mobile bottom sheets and confirmation
 * screens. Encodes the shared sheet recipe (rounded-xl, text-sm, no shadow) and
 * uses Ionic primitives (IonIcon / IonSpinner) — distinct from the cross-surface
 * @shared/ui/Button, which targets the web look (rounded-lg, shadow, Lucide).
 */
const ProActionButton: React.FC<ProActionButtonProps> = ({
  children,
  onClick,
  variant = 'danger',
  icon,
  loading = false,
  disabled = false,
  type = 'button',
  className = '',
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${BASE_CLASSES} ${VARIANT_CLASSES[variant]} ${className}`.trim()}
    >
      {loading ? (
        <IonSpinner
          name="crescent"
          className={variant === 'danger' ? 'text-white' : undefined}
        />
      ) : (
        <>
          {icon ? <IonIcon icon={icon} className="text-lg" /> : null}
          {children}
        </>
      )}
    </button>
  );
};

export default ProActionButton;
